import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DmsService } from '../../services/Dms.service';

export interface DmsState {
  loggedIn: boolean;
  loading: boolean;
  error?: string;
  downloadPath: string | null;
}

const getDownloadPathFromLocalStorage = (): string | null => {
  const downloadPath = localStorage.getItem('dmsDownloadPath');
  return downloadPath || null;
};

const initialState: DmsState = {
  loggedIn: false,
  loading: false,
  error: undefined,
  downloadPath: getDownloadPathFromLocalStorage(),
};

// 1️⃣ Get the DMS auth URL
export const getDmsAuthUrl = createAsyncThunk<
  string, // return type
  void, // arg type
  { rejectValue: string }
>('dms/getAuthUrl', async (_, { rejectWithValue }) => {
  try {
    return await DmsService.getAuthUrl();
  } catch {
    return rejectWithValue('Failed to get DMS auth URL');
  }
});

// 2️⃣ Exchange the code for tokens (server stores them in session)
export const exchangeDmsCode = createAsyncThunk<
  void,
  string,
  { rejectValue: string }
>('dms/exchangeCode', async (code, { rejectWithValue }) => {
  try {
    await DmsService.exchangeDmsCode(code);
  } catch {
    return rejectWithValue('DMS token exchange failed');
  }
});

// 3️⃣ Check “loggedIn” status
export const checkDmsStatus = createAsyncThunk<
  boolean, // return type
  void, // no arg
  { rejectValue: string }
>('dms/checkStatus', async (_, { rejectWithValue }) => {
  try {
    return await DmsService.checkDmsStatus();
  } catch {
    return rejectWithValue('Failed to check DMS status');
  }
});

// 4️⃣ Logout (clears only the DMS session slice on the server)
export const logoutDms = createAsyncThunk<void, void, { rejectValue: string }>(
  'dms/logout',
  async (_, { rejectWithValue }) => {
    try {
      await DmsService.logoutDms();
    } catch {
      return rejectWithValue('DMS logout failed');
    }
  },
);

const dmsSlice = createSlice({
  name: 'dms',
  initialState,
  reducers: {
    setDMSDownloadPath(state, action: PayloadAction<string>) {
      state.downloadPath = action.payload;
      localStorage.setItem('dmsDownloadPath', action.payload);
    },
    clearDMSDownloadPath(state) {
      state.downloadPath = null;
      localStorage.removeItem('dmsDownloadPath');
    },
  },
  extraReducers: (builder) => {
    // getAuthUrl
    builder
      .addCase(getDmsAuthUrl.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(getDmsAuthUrl.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(getDmsAuthUrl.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // exchangeDmsCode
    builder
      .addCase(exchangeDmsCode.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(exchangeDmsCode.fulfilled, (state) => {
        state.loading = false;
        state.loggedIn = true;
      })
      .addCase(exchangeDmsCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // checkDmsStatus
    builder
      .addCase(checkDmsStatus.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        checkDmsStatus.fulfilled,
        (state, action: PayloadAction<boolean>) => {
          state.loading = false;
          state.loggedIn = action.payload;
        },
      )
      .addCase(checkDmsStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // logoutDms
    builder
      .addCase(logoutDms.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(logoutDms.fulfilled, (state) => {
        state.loading = false;
        state.loggedIn = false;
      })
      .addCase(logoutDms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});
export const { setDMSDownloadPath, clearDMSDownloadPath } = dmsSlice.actions;

export default dmsSlice.reducer;

// Selectors:
export const selectDmsLoggedIn = (state: any) => state.dms.loggedIn;
export const selectDmsLoading = (state: any) => state.dms.loading;
export const selectDmsError = (state: any) => state.dms.error;
export const selectDMSDownloadPath = (state: any) => state.dms.downloadPath;
